"use client";

import { useMemo } from "react";
import Button from "@/components/common/Button";
import FullView from "@/components/common/FullView";
import { PATH } from "@/constants/path";
import { useBatchImpressionTracking } from "@/hooks/useImpressionTracking";
import { trackEvent } from "@/utils/analytics";
import { cn } from "@/utils/cn";

// 섹션 컨테이너
const sectionClasses = cn(
  "relative flex h-full w-full max-w-[1200px] flex-col items-center",
  "desktop:flex-row desktop:items-center desktop:justify-between",
  "desktop:gap-12",
);

// 왼쪽 텍스트 + 버튼 컨테이너
const leftContainerClasses = cn(
  "z-10 flex flex-col gap-8",
  "items-center px-side",
  "desktop:max-w-[550px] desktop:flex-shrink-0",
  "desktop:items-start desktop:px-0",
);

// 메인 타이틀
const titleClasses = cn("text-center text-36 font-bold", "leading-[1.5] tracking-tight", "desktop:text-left desktop:text-44");

// 서브 텍스트
const subtextClasses = cn(
  "text-14 text-center",
  "leading-[1.45] tracking-tight text-black/55",
  "tablet:text-18",
  "desktop:text-24 desktop:text-left",
);

// 버튼 영역
const buttonGroupClasses = cn("flex flex-wrap gap-4", "justify-center", "desktop:justify-start");

// 커스텀 버튼 스타일
const blackButtonClasses = cn(
  "rounded-xl font-medium leading-[1.45]",
  "bg-black text-white",
  "text-14 px-4 py-2.5",
  "tablet:text-18",
  "desktop:text-18",
  "hidden header:inline-flex",
);

const whiteButtonClasses = cn(
  "rounded-xl font-medium leading-[1.45]",
  "bg-white text-black border-2 border-black/15",
  "text-14 px-4 py-2.5",
  "tablet:text-18",
  "desktop:text-18",
);

// 오른쪽 이미지 외부 컨테이너
const imageOuterClasses = cn(
  "relative w-full",
  "mt-12",
  "desktop:mt-0 desktop:flex-1 desktop:max-w-[900px]",
  "desktop:flex desktop:justify-end",
);

function HeroSection() {
  const impressionEvents = useMemo(
    () => [
      { eventName: "cta_impression", params: { cta_type: "demo", cta_position: "hero" } },
      { eventName: "download_btn_impression", params: { file_name: "company_deck", cta_position: "hero" } },
    ],
    [],
  );

  const heroRef = useBatchImpressionTracking<HTMLDivElement>(impressionEvents);

  const handleDemoClick = () => {
    trackEvent("cta_click", { cta_type: "demo", cta_position: "hero" });
  };

  const handleDownloadClick = () => {
    trackEvent("file_download", { file_name: "company_deck", cta_position: "hero" });
  };

  return (
    <FullView as="section" className="w-full">
      <div ref={heroRef} className={sectionClasses}>
        {/* 왼쪽 텍스트 + 버튼 영역 */}
        <div className={leftContainerClasses}>
          {/* 메인 타이틀 */}
          <div className={titleClasses}>
            <h2 className="mb-0">채용 공고로</h2>
            <h2 className="mb-0">만날 수 없는 핵심 인재</h2>
            <h2>1주 안에 커피챗까지</h2>
          </div>

          {/* 서브 텍스트 */}
          <div className={subtextClasses}>
            <p className="mb-0">희소 포지션·시니어·리드급 채용에 어려움을 겪는 팀을 위해</p>
            <p>AI 리크루터로 숨은 인재를 발굴하여 연결합니다.</p>
          </div>

          {/* 버튼 영역 */}
          <div className={buttonGroupClasses}>
            <Button href={PATH.DEMO_CLIENT} className={blackButtonClasses} onClick={handleDemoClick}>
              서비스 체험하기
            </Button>
            <div className="flex w-full flex-col gap-4 header:w-auto header:flex-row">
              <Button
                href={PATH.COMPANY_INTRO}
                className={cn(whiteButtonClasses, "flex w-full items-center justify-center header:w-auto")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDownloadClick}
              >
                서비스 소개서 다운받기
              </Button>
              <Button href="/request" className={cn(blackButtonClasses, "flex w-full items-center justify-center header:hidden")}>
                문의하기
              </Button>
            </div>
          </div>
        </div>

        {/* 오른쪽 이미지 영역 */}
        <div className={imageOuterClasses}>
          <picture>
            <source media="(min-width: 1024px)" srcSet="/image/home/hero-desktop.png" />
            <source media="(min-width: 768px)" srcSet="/image/home/hero-tablet.png" />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/image/home/hero-mobile.png"
              alt="AI 리크루터 플랫폼"
              fetchPriority="high"
              className="w-full object-contain aspect-[1428/1588] tablet:aspect-[3129/2009] desktop:aspect-[2704/2416]"
            />
          </picture>
        </div>
      </div>
    </FullView>
  );
}

export default HeroSection;
